import React, { useState, useEffect, useMemo, memo } from 'react';
import { CBH } from './interpolate.js';
import './jptr-inkcurve.css';


function SvgGuideLineV({ y, width = 100, height = 100 }) {
    return (
        <line x1={0} y1={y} x2={width} y2={y} stroke="#000000" strokeWidth="0.1" />
    );
}

function SvgGuideLineH({ x, width = 100, height = 100 }) {
    return (
        <line x1={x} y1={0} x2={x} y2={height} stroke="#000000" strokeWidth="0.1" />
    );
}

function SnakePoint({ pos, color }) {

    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    return (
        /*  <circle cx={pos.x} cy={pos.y} r={0.75} fill={color} onMouseEnter={handleMouseEnter} style={{ position: 'relative' }} /> */
        <>
            <circle
                cx={pos.x}
                cy={pos.y}
                r={0.75}
                fill={color}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ position: 'relative' }}
            />
            {isHovered && (
                <g>
                    <rect
                        x={pos.x + 3}
                        y={pos.y + 0}
                        rx={0.5}
                        ry={0.5}
                        width={13}
                        height={5}
                        fill="lightgray"
                        opacity={0.8}
                    />
                    <text x={pos.x + 4} y={pos.y + 3.25} style={{ fontSize: '2.5px', fill: 'black', zIndex: 10 }} >
                        <tspan fill={color}>{`${pos.x.toFixed(1)}`}</tspan> <tspan fontWeight="bold">{`${(100-pos.y).toFixed(1)}`}</tspan>
                    </text>
                </g>
            )}
        </>
    );
}

function BezierPoint({ bpt, cpt, onChange, className, color }) {

    const handleDrag = (event, type) => {
        event.preventDefault();

        const svg = event.target.ownerSVGElement;
        const CTM = svg.getScreenCTM();

        const handleMouseMove = (e) => {
            e.preventDefault();
            const npt = {
                x: (e.clientX - CTM.e) / CTM.a,
                y: (e.clientY - CTM.f) / CTM.d,
            };

            type === 'cpt' ? onChange(npt, bpt) : onChange(cpt, npt);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <g className="bezier-point-group">
            <line x1={cpt.x} y1={cpt.y} x2={bpt.x} y2={bpt.y} className={className} />
            <circle cx={bpt.x} cy={bpt.y} r={0.75} fill={color} />
            <circle cx={bpt.x} cy={bpt.y} r={1.5} onMouseDown={(e) => handleDrag(e, 'bpt')} className={className} fill='transparent' />
            <circle cx={cpt.x} cy={cpt.y} r={1.25} onMouseDown={(e) => handleDrag(e, 'cpt')} className={className} fill={color} />
        </g>
    );
}

const InkCurves2 = memo(({ curveColor, pts, onBezierPointsChange }) => {

    const [numGuidesVertical,] = useState(5);
    const [numGuidesHorizontal,] = useState(11);

    const verticalGuides = useMemo(() => {
        return Array.from({ length: numGuidesVertical }).map((_, i) => (
            <SvgGuideLineV key={`v-${i}`} y={i * 100 / (numGuidesVertical - 1)} />
        ));
    }, [numGuidesVertical]);

    const horizontalGuides = useMemo(() => {
        return Array.from({ length: numGuidesHorizontal }).map((_, i) => (
            <SvgGuideLineH key={`h-${i}`} x={i * 100 / (numGuidesHorizontal - 1)} />
        ));
    }, [numGuidesHorizontal]);

    const [snakePoints, setSnakePoints] = useState(
        Array.from({ length: numGuidesHorizontal }, (_, i) => ({ x: i * 100 / (numGuidesHorizontal - 1), y: 0 }))
    );

    useEffect(() => {
        if (pts && pts.length > 1) {
            setSnakePoints((prevSnakePoints) =>
                prevSnakePoints.map((point) => {
                    const { y } = CBH.interpolateBezier(point.x, pts[0].cpt, pts[0].bpt, pts[1].bpt, pts[1].cpt, 0.00001);
                    return { ...point, y };
                })
            );
        }
    }, [pts]);

    const handleChangeLight = (cpt, bpt) => {
        const cptY = Math.min(100, Math.max(0, cpt.y));
        const bptX = Math.min(100, Math.max(0, bpt.x));
        const bptY = Math.min(100, Math.max(0, bpt.y));

        onBezierPointsChange([{ 'cpt': { x: 0, y: cptY }, 'bpt': { x: bptX, y: bptY } }, pts[1]])
    };
    const handelChangeFull = (cpt, bpt) => {
        const cptY = Math.min(100, Math.max(0, cpt.y));
        const bptX = Math.min(100, Math.max(0, bpt.x));
        const bptY = Math.min(100, Math.max(0, bpt.y));

        onBezierPointsChange([pts[0], { 'cpt': { x: 100, y: cptY }, 'bpt': { x: bptX, y: bptY } }])
    };

    return (
        <div className='ink-curves-main'>

            <svg id="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                <defs>
                    <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" style={{ stopColor: 'white', stopOpacity: 0.55 }} />
                        <stop offset="100%" style={{ stopColor: `${curveColor}`, stopOpacity: 0.85 }} />
                    </linearGradient>
                </defs>
                <g id="guidesV">{verticalGuides}</g>
                <g id="guidesH">{horizontalGuides}</g>

                <path id="curve"
                    d={`M ${pts[0].cpt.x} ${pts[0].cpt.y}
                        C  ${pts[0].bpt.x} ${pts[0].bpt.y}, ${pts[1].bpt.x} ${pts[1].bpt.y}, ${pts[1].cpt.x} ${pts[1].cpt.y}
                        L ${pts[1].cpt.x + 100} ${pts[1].cpt.y}
                        L 110 110
                        L -10 110
                        L ${pts[0].cpt.x - 100} ${pts[0].cpt.y}
                        Z`}
                    stroke={curveColor}
                    fill="url(#bgGradient)"
                    strokeWidth="0.3" 
                />

                {snakePoints.map((point, i) => <SnakePoint key={i} pos={point} color={curveColor} />)}

                <BezierPoint cpt={pts[0].cpt} bpt={pts[0].bpt} color={curveColor} className="bzr-light" onChange={handleChangeLight} />
                <BezierPoint cpt={pts[1].cpt} bpt={pts[1].bpt} color={curveColor} className="bzr-full" onChange={handelChangeFull} />

            </svg>
        </div>
    )
});

export {
    InkCurves2
};